import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import {
  Bell, CalendarBlank, CreditCard, Exam, Bus, ChatCircleText, PencilSimple, Trash, Plus,
} from "@phosphor-icons/react";

const ICONS = { attendance: CalendarBlank, fee: CreditCard, exam: Exam, bus: Bus, message: ChatCircleText };

const FIELDS = [
  { name: "type", label: "Type", type: "select", options: [
    { value: "attendance", label: "Attendance" }, { value: "fee", label: "Fee" },
    { value: "exam", label: "Exam" }, { value: "bus", label: "Bus" }, { value: "message", label: "Message" },
  ]},
  { name: "title", label: "Notification title", type: "textarea" },
];

function timeAgo(iso) {
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

export default function Parent() {
  const { t } = useTranslation();
  const [feed, setFeed] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/parent/feed");
    setFeed(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (n) => setModal({ open: true, editing: { ...n, id: n.id } });
  const close = () => setModal({ open: false, editing: null });

  const save = async (values) => {
    try {
      if (modal.editing) {
        await api.patch(`/parent/feed/${modal.editing.id}`, { type: values.type, title: values.title });
        toast.success("Notification updated");
      } else {
        await api.post("/parent/feed", { type: values.type, title: values.title });
        toast.success("Notification sent to parent");
      }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete notification?`)) return;
    try { await api.delete(`/parent/feed/${n.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <PageHeader
        overline="M09 · Assessment"
        title={t("modules.parent.title")}
        description={t("modules.parent.subtitle")}
        action={
          <Button onClick={openCreate} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-notif-btn">
            <Plus size={14} className="mr-1.5"/> Post notification
          </Button>
        }
      />
      <div className="p-6 lg:p-8 grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Bell size={16} weight="duotone"/>
            <div className="font-heading font-semibold text-base">Live notifications · Aarav S. · B.Tech CSE Y2</div>
          </div>
          <div className="divide-y divide-border">
            {feed.map(n => {
              const I = ICONS[n.type] || Bell;
              return (
                <div key={n.id} className="p-4 flex items-start gap-3 hover:bg-secondary/40" data-testid={`feed-${n.id}`}>
                  <div className="w-9 h-9 rounded-md bg-secondary flex items-center justify-center shrink-0">
                    <I size={16} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.time)} · {n.type}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" onClick={() => openEdit(n)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${n.id}`}><PencilSimple size={13}/></Button>
                    <Button variant="ghost" onClick={() => remove(n)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${n.id}`}><Trash size={13}/></Button>
                  </div>
                </div>
              );
            })}
            {feed.length === 0 && (
              <div className="p-10 text-center text-sm text-muted-foreground">No notifications yet. Click "Post notification" to send one.</div>
            )}
          </div>
        </div>
        <div className="lg:col-span-4 space-y-5">
          <div className="eos-card">
            <div className="eos-overline">Quick view</div>
            <div className="font-heading font-semibold text-base mt-1">Aarav S.</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">CGPA</span><span className="font-mono-eos font-bold">8.92</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Attendance</span><span className="font-mono-eos">92.1%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fees</span><span className="text-emerald-700">Paid</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Bus route</span><span>B-14 · 6m ETA</span></div>
            </div>
          </div>
          <div className="eos-card">
            <div className="eos-overline">Channels</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex justify-between"><span>Push</span><span className="text-emerald-700">On</span></li>
              <li className="flex justify-between"><span>SMS</span><span className="text-emerald-700">On</span></li>
              <li className="flex justify-between"><span>WhatsApp</span><span className="text-emerald-700">On</span></li>
              <li className="flex justify-between"><span>Email</span><span className="text-muted-foreground">Weekly digest</span></li>
            </ul>
          </div>
        </div>
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit notification" : "Post new notification"}
        fields={FIELDS}
        initial={modal.editing || { type: "message", title: "" }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Send to parent"}
      />
    </div>
  );
}
